import { NotificationManager } from 'react-notifications';
import { apiRequest } from 'Api/apiHelpers';

export const HR_DASHBOARD_REQUEST = 'HR_DASHBOARD_REQUEST';
export const HR_DASHBOARD_SUCCESS = 'HR_DASHBOARD_SUCCESS';
export const HR_DASHBOARD_FAILURE = 'HR_DASHBOARD_FAILURE';

export const HR_SETTINGS_REQUEST = 'HR_SETTINGS_REQUEST';
export const HR_SETTINGS_SUCCESS = 'HR_SETTINGS_SUCCESS';
export const HR_SETTINGS_FAILURE = 'HR_SETTINGS_FAILURE';

export const HR_SETTINGS_SAVE_REQUEST = 'HR_SETTINGS_SAVE_REQUEST';
export const HR_SETTINGS_SAVE_SUCCESS = 'HR_SETTINGS_SAVE_SUCCESS';
export const HR_SETTINGS_SAVE_FAILURE = 'HR_SETTINGS_SAVE_FAILURE';

export const HR_IMPORT_REQUEST = 'HR_IMPORT_REQUEST';
export const HR_IMPORT_SUCCESS = 'HR_IMPORT_SUCCESS';
export const HR_IMPORT_FAILURE = 'HR_IMPORT_FAILURE';

export const HR_EMPLOYEE_SEARCH_REQUEST = 'HR_EMPLOYEE_SEARCH_REQUEST';
export const HR_EMPLOYEE_SEARCH_SUCCESS = 'HR_EMPLOYEE_SEARCH_SUCCESS';
export const HR_EMPLOYEE_SEARCH_FAILURE = 'HR_EMPLOYEE_SEARCH_FAILURE';
export const HR_EMPLOYEE_SEARCH_CLEAR = 'HR_EMPLOYEE_SEARCH_CLEAR';

export const HR_COMPANIES_REQUEST = 'HR_COMPANIES_REQUEST';
export const HR_COMPANIES_SUCCESS = 'HR_COMPANIES_SUCCESS';
export const HR_COMPANIES_FAILURE = 'HR_COMPANIES_FAILURE';
export const HR_CONTEXT_REQUEST = 'HR_CONTEXT_REQUEST';
export const HR_CONTEXT_SUCCESS = 'HR_CONTEXT_SUCCESS';
export const HR_CONTEXT_FAILURE = 'HR_CONTEXT_FAILURE';
export const HR_SNAPSHOTS_REQUEST = 'HR_SNAPSHOTS_REQUEST';
export const HR_SNAPSHOTS_SUCCESS = 'HR_SNAPSHOTS_SUCCESS';
export const HR_SNAPSHOTS_FAILURE = 'HR_SNAPSHOTS_FAILURE';
export const HR_SNAPSHOT_SAVE_REQUEST = 'HR_SNAPSHOT_SAVE_REQUEST';
export const HR_SNAPSHOT_SAVE_SUCCESS = 'HR_SNAPSHOT_SAVE_SUCCESS';
export const HR_SNAPSHOT_SAVE_FAILURE = 'HR_SNAPSHOT_SAVE_FAILURE';
export const HR_SNAPSHOT_LOAD_REQUEST = 'HR_SNAPSHOT_LOAD_REQUEST';
export const HR_SNAPSHOT_LOAD_SUCCESS = 'HR_SNAPSHOT_LOAD_SUCCESS';
export const HR_SNAPSHOT_LOAD_FAILURE = 'HR_SNAPSHOT_LOAD_FAILURE';

const HR_API_BASE = `${(
  process.env.REACT_APP_BASE_URL || 'http://localhost:5228/api/'
).replace(/\/+$/, '')}/hr-analytics`;

// Unique sequence also distinguishes rapid logout/login cycles for the same user.
let hrCompaniesRequestSequence = 0;

// A request key lets reducers ignore results that arrive after the user has
// changed company, division, reporting date, or selected snapshot.
const getActiveHrCompanyId = () =>
  localStorage.getItem('selectedCompanyId') ||
  localStorage.getItem('hrAnalyticsSelectedCompanyId') ||
  'base-company';

const hrRequestKey = (...parts) => [
  getActiveHrCompanyId(),
  ...parts.map((part) => String(part ?? ''))
].join('::');

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.response?.data?.error ||
  (typeof error?.response?.data === 'string' ? error.response.data : null) ||
  error?.message ||
  fallback;

export const fetchHrDashboard = (asOfDate, division) => async (dispatch) => {
  const requestKey = hrRequestKey('dashboard', asOfDate || '', division || 'ALL');
  dispatch({ type: HR_DASHBOARD_REQUEST, meta: { requestKey } });
  try {
    const params = {};
    if (asOfDate) params.asOfDate = asOfDate;
    if (division) params.division = division;

    const response = await apiRequest('get', `${HR_API_BASE}/dashboard`, null, params);
    dispatch({ type: HR_DASHBOARD_SUCCESS, payload: response.data, meta: { requestKey } });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error, 'Failed to load HR Analytics dashboard.');
    dispatch({ type: HR_DASHBOARD_FAILURE, payload: message, meta: { requestKey } });
    NotificationManager.error(message);
    throw error;
  }
};

export const fetchHrSettings = () => async (dispatch) => {
  const requestKey = hrRequestKey('settings-load');
  dispatch({ type: HR_SETTINGS_REQUEST, meta: { requestKey } });
  try {
    const response = await apiRequest('get', `${HR_API_BASE}/settings`);
    dispatch({ type: HR_SETTINGS_SUCCESS, payload: response.data, meta: { requestKey } });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error, 'Failed to load HR Analytics settings.');
    dispatch({ type: HR_SETTINGS_FAILURE, payload: message, meta: { requestKey } });
    NotificationManager.error(message);
    throw error;
  }
};

export const updateHrSettings = (payload) => async (dispatch) => {
  const requestKey = hrRequestKey('settings-save');
  dispatch({ type: HR_SETTINGS_SAVE_REQUEST, meta: { requestKey } });
  try {
    const response = await apiRequest('put', `${HR_API_BASE}/settings`, payload);
    dispatch({ type: HR_SETTINGS_SAVE_SUCCESS, payload: response.data, meta: { requestKey } });
    NotificationManager.success('HR Analytics settings saved successfully.');
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error, 'Failed to save HR Analytics settings.');
    dispatch({ type: HR_SETTINGS_SAVE_FAILURE, payload: message, meta: { requestKey } });
    NotificationManager.error(message);
    throw error;
  }
};

export const importHrWorkbook = (file) => async (dispatch) => {
  if (!file) return null;

  dispatch({ type: HR_IMPORT_REQUEST });
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiRequest('post', `${HR_API_BASE}/import`, formData);
    dispatch({ type: HR_IMPORT_SUCCESS, payload: response.data });

    const result = response.data || {};
    const warnings = Number(result.warningCount || 0);
    const message = warnings > 0
      ? `HR data imported with ${warnings} warning${warnings === 1 ? '' : 's'}.`
      : 'HR data imported successfully.';
    NotificationManager.success(message);

    return result;
  } catch (error) {
    const message = getErrorMessage(error, 'HR data import failed.');
    dispatch({ type: HR_IMPORT_FAILURE, payload: message });
    NotificationManager.error(message);
    throw error;
  }
};

export const searchHrEmployees = (query, division) => async (dispatch) => {
  const trimmed = String(query || '').trim();
  if (trimmed.length < 2) {
    dispatch({ type: HR_EMPLOYEE_SEARCH_CLEAR });
    return [];
  }

  dispatch({ type: HR_EMPLOYEE_SEARCH_REQUEST });
  try {
    const response = await apiRequest(
      'get',
      `${HR_API_BASE}/employees/search`,
      null,
      { q: trimmed, ...(division ? { division } : {}) }
    );
    dispatch({ type: HR_EMPLOYEE_SEARCH_SUCCESS, payload: response.data || [] });
    return response.data || [];
  } catch (error) {
    const message = getErrorMessage(error, 'Employee search failed.');
    dispatch({ type: HR_EMPLOYEE_SEARCH_FAILURE, payload: message });
    return [];
  }
};

export const clearHrEmployeeSearch = () => ({ type: HR_EMPLOYEE_SEARCH_CLEAR });

export const fetchHrCompanies = () => async (dispatch, getState) => {
  // Prevent a late response from a previous login/session replacing this user's
  // company choices after HR_SESSION_RESET has run. The sequence also makes the
  // request identity unique when the same user logs out and quickly logs back in.
  const requestKey = hrRequestKey(
    'companies',
    localStorage.getItem('userId') || '',
    localStorage.getItem('companyId') || '',
    ++hrCompaniesRequestSequence
  );
  const isCurrentRequest = () =>
    getState()?.hrAnalytics?.companiesRequestKey === requestKey;

  dispatch({ type: HR_COMPANIES_REQUEST, meta: { requestKey } });
  try {
    const response = await apiRequest('get', `${HR_API_BASE}/companies`);
    const companies = Array.isArray(response.data) ? response.data : [];

    // Do not let callers (including the Overview initialization effect) act on
    // stale company options even though the reducer would also reject the action.
    if (!isCurrentRequest()) return [];

    dispatch({ type: HR_COMPANIES_SUCCESS, payload: companies, meta: { requestKey } });
    return companies;
  } catch (error) {
    if (!isCurrentRequest()) return [];

    const message = getErrorMessage(error, 'Failed to load accessible companies.');
    dispatch({ type: HR_COMPANIES_FAILURE, payload: message, meta: { requestKey } });
    NotificationManager.error(message);
    throw error;
  }
};

export const fetchHrContext = (division) => async (dispatch) => {
  const requestKey = hrRequestKey('context', division || 'ALL');
  dispatch({ type: HR_CONTEXT_REQUEST, meta: { requestKey } });
  try {
    const response = await apiRequest('get', `${HR_API_BASE}/context`, null, division ? { division } : {});
    dispatch({ type: HR_CONTEXT_SUCCESS, payload: response.data, meta: { requestKey } });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error, 'Failed to load HR Analytics access context.');
    dispatch({ type: HR_CONTEXT_FAILURE, payload: message, meta: { requestKey } });
    throw error;
  }
};

export const fetchHrSnapshots = (division) => async (dispatch) => {
  const requestKey = hrRequestKey('snapshots-list', division || 'ALL');
  dispatch({ type: HR_SNAPSHOTS_REQUEST, meta: { requestKey } });
  try {
    const response = await apiRequest('get', `${HR_API_BASE}/snapshots`, null, division ? { division } : {});
    const snapshots = Array.isArray(response.data) ? response.data : [];
    dispatch({ type: HR_SNAPSHOTS_SUCCESS, payload: snapshots, meta: { requestKey } });
    return snapshots;
  } catch (error) {
    const message = getErrorMessage(error, 'Failed to load saved monthly reports.');
    dispatch({ type: HR_SNAPSHOTS_FAILURE, payload: message, meta: { requestKey } });
    NotificationManager.error(message);
    throw error;
  }
};

export const saveHrSnapshot = (reportingDate, division) => async (dispatch) => {
  const requestKey = hrRequestKey('snapshot-save', reportingDate || '', division || 'ALL');
  dispatch({ type: HR_SNAPSHOT_SAVE_REQUEST, meta: { requestKey } });
  try {
    const response = await apiRequest(
      'post',
      `${HR_API_BASE}/snapshots`,
      { reportingDate },
      division ? { division } : {}
    );
    dispatch({ type: HR_SNAPSHOT_SAVE_SUCCESS, payload: response.data, meta: { requestKey } });
    NotificationManager.success('Monthly HR Analytics snapshot saved.');
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error, 'Failed to save monthly snapshot.');
    dispatch({ type: HR_SNAPSHOT_SAVE_FAILURE, payload: message, meta: { requestKey } });
    NotificationManager.error(message);
    throw error;
  }
};

export const fetchHrSnapshot = (snapshotId, division) => async (dispatch) => {
  const requestKey = hrRequestKey('snapshot-load', snapshotId, division || 'ALL');
  dispatch({ type: HR_SNAPSHOT_LOAD_REQUEST, meta: { requestKey } });
  try {
    const response = await apiRequest(
      'get',
      `${HR_API_BASE}/snapshots/${snapshotId}`,
      null,
      division ? { division } : {}
    );
    dispatch({ type: HR_SNAPSHOT_LOAD_SUCCESS, payload: response.data, meta: { requestKey } });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error, 'Failed to load saved monthly snapshot.');
    dispatch({ type: HR_SNAPSHOT_LOAD_FAILURE, payload: message, meta: { requestKey } });
    NotificationManager.error(message);
    throw error;
  }
};

